import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getAccessToken } from "../lib/supabaseClient";
import {
  Loader2, Search, RefreshCw, MapPin, Building2, ArrowLeft, ChevronRight,
  CheckCircle2, XCircle, AlertCircle, Clock, ShieldCheck, PauseCircle,
  Send, Mail, Phone, Users, User, Calendar, FileText, Activity, Wallet,
  Info, Edit3, Copy, Download, ExternalLink, RotateCw, Trash2, Eye, Store, Plus, X,
} from "lucide-react";
import { formatEnum, formatBool, formatArray, formatFinancialTerm, formatContractTerm } from "../lib/labels";
import { ListingPrepTab } from "./AdminOperatorMarketplace";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function af(path, opts = {}) {
  const t = await getAccessToken();
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${t || ""}` },
  });
}

// ---------- Status vocabulary ----------
const STATUS_META = {
  draft:               { label: "Draft",              tone: "gray"  },
  submitted:           { label: "Submitted",          tone: "blue"  },
  under_review:        { label: "Under Review",       tone: "amber" },
  more_info_requested: { label: "Info Needed",        tone: "amber" },
  manager_interested:  { label: "Manager Interested", tone: "gold"  },
  agreement_pending:   { label: "Agreement Pending",  tone: "amber" },
  agreement_signed:    { label: "Agreement Signed",   tone: "green" },
  verified:            { label: "Verified",           tone: "green" },
  approved:            { label: "Verified",           tone: "green" }, // legacy display map
  marketplace_ready:   { label: "Marketplace Ready",  tone: "green" },
  sold:                { label: "Sold",               tone: "green" },
  declined:            { label: "Declined",           tone: "red"   },
  rejected:            { label: "Rejected",           tone: "red"   },
  on_hold:             { label: "On Hold",            tone: "gray"  },
  decision_keep:       { label: "Keep · SpotBounty",  tone: "gold"  },
  decision_operator:   { label: "For Operator",       tone: "gold"  },
  decision_hold:       { label: "On Hold",            tone: "gray"  },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status || "—", tone: "gray" };
  return (
    <span className={`sb-pill sb-pill-${m.tone}`} data-testid={`admin-loc-pill-${status}`}>
      {m.label}
    </span>
  );
}

// ---------- Root section ----------
export default function AdminLocationsSection() {
  const [selectedId, setSelectedId] = useState(null);
  return selectedId
    ? <LocationDetail id={selectedId} onBack={() => setSelectedId(null)} />
    : <LocationsList onOpen={setSelectedId} />;
}

// ---------- List ----------
function LocationsList({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (statusFilter) params.set("status", statusFilter);
    if (stateFilter) params.set("state", stateFilter);
    params.set("limit", "200");
    const r = await af(`/api/admin/locations?${params.toString()}`);
    const d = await r.json().catch(() => ({ rows: [], total: 0 }));
    setRows(d.rows || []); setTotal(d.total || 0); setLoading(false);
  }, [q, statusFilter, stateFilter]);

  useEffect(() => { load(); }, [load]);

  // Client-computed summary counts (accurate for the current filter result).
  const counts = useMemo(() => {
    const c = { total: rows.length, draft: 0, submitted: 0, under_review: 0,
                verified: 0, rejected: 0, decision_keep: 0, decision_operator: 0, decision_hold: 0 };
    for (const r of rows) if (r.status in c) c[r.status]++;
    return c;
  }, [rows]);

  const summaryCards = [
    { key: "total",             label: "Total",         tone: "text-white" },
    { key: "draft",             label: "Draft",         tone: "text-[#A1A1AA]" },
    { key: "submitted",         label: "Submitted",     tone: "text-[#3B82F6]" },
    { key: "under_review",      label: "Under Review",  tone: "text-[#F59E0B]" },
    { key: "verified",          label: "Verified",      tone: "text-[#22C55E]" },
    { key: "rejected",          label: "Rejected",      tone: "text-[#EF4444]" },
    { key: "decision_keep",     label: "Keep",          tone: "text-[#FFC107]" },
    { key: "decision_operator", label: "For Operator",  tone: "text-[#FFC107]" },
    { key: "decision_hold",     label: "On Hold",       tone: "text-[#A1A1AA]" },
  ];

  return (
    <>
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="sb-eyebrow">Location Marketplace</div>
          <h1 className="mt-1 sb-headline text-[32px] md:text-[40px]">
            VENDING <span className="text-[#FFC107]">LOCATIONS.</span>
          </h1>
          <div className="text-[13px] text-[#A1A1AA] mt-1">Scout submissions, verification workflow and manager outreach.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }} data-testid="admin-loc-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={() => setNewOpen(true)} className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }} data-testid="admin-loc-new">
            <Plus size={13} /> New Location
          </button>
        </div>
      </header>

      {/* Summary strip */}
      <section className="grid grid-cols-3 md:grid-cols-5 xl:grid-cols-9 gap-3" data-testid="admin-loc-summary">
        {summaryCards.map((c) => (
          <div key={c.key} className="sb-card p-4">
            <div className="sb-eyebrow" style={{ fontSize: 9 }}>{c.label}</div>
            <div className={`mt-2 text-[18px] font-black ${c.tone}`}>{counts[c.key] ?? 0}</div>
          </div>
        ))}
      </section>

      {/* Filters */}
      <section className="sb-card p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search business or city…"
              className="sb-input pl-9"
              data-testid="admin-loc-search"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="sb-input max-w-[200px]" data-testid="admin-loc-status-filter">
            <option value="">All statuses</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <input
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
            placeholder="State (e.g. IL)"
            maxLength={2}
            className="sb-input max-w-[140px] uppercase"
            data-testid="admin-loc-state-filter"
          />
          <div className="text-[11.5px] text-[#A1A1AA] ml-auto">{total} total · showing {rows.length}</div>
        </div>
      </section>

      {/* Desktop table */}
      <section className="sb-card p-0 overflow-hidden hidden md:block">
        {loading && (
          <div className="p-8 text-[#A1A1AA] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading locations…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center">
            <div className="mx-auto w-12 h-12 rounded-lg bg-[#FFC107]/10 text-[#FFC107] flex items-center justify-center"><MapPin size={20} /></div>
            <div className="mt-3 font-black">No locations yet</div>
            <div className="mt-1 text-[13px] text-[#A1A1AA]">Scout submissions will appear here as they arrive.</div>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left border-b border-[#2A2F34] text-[10.5px] uppercase tracking-widest text-[#A1A1AA]">
                <th className="px-4 py-3">Business</th>
                <th className="px-4 py-3">City / State</th>
                <th className="px-4 py-3">Scout</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}
                    onClick={() => onOpen(r.id)}
                    className="border-b border-[#2A2F34] last:border-0 hover:bg-white/[.03] cursor-pointer"
                    data-testid={`admin-loc-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-bold">{r.business_name || "Untitled"}</div>
                  </td>
                  <td className="px-4 py-3 text-[#A1A1AA]">
                    {r.city ? `${r.city}${r.state ? `, ${r.state}` : ""}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-[#A1A1AA]">
                    <div className="truncate max-w-[180px]">{r.scout_name || r.scout_email || "—"}</div>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                  <td className="px-4 py-3 text-[#A1A1AA]">
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight size={14} className="text-[#52525b]" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Mobile card list */}
      <section className="md:hidden space-y-3">
        {loading && <div className="text-[#A1A1AA] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {!loading && rows.map((r) => (
          <button key={r.id} onClick={() => onOpen(r.id)}
                  className="sb-card p-4 w-full text-left flex items-start justify-between gap-3"
                  data-testid={`admin-loc-mrow-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-bold truncate">{r.business_name || "Untitled"}</div>
              <div className="text-[11.5px] text-[#A1A1AA] mt-0.5 truncate">
                {r.city ? `${r.city}${r.state ? `, ${r.state}` : ""}` : "no address"} · {r.scout_email || (r.source === "owner" ? "Owner-created" : (r.source === "business" ? "Business-direct" : "—"))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <StatusPill status={r.status} />
                {r.source && r.source !== "scout" && (
                  <span className="text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-[#FFC107]/40 text-[#FFC107] bg-[#FFC107]/5" data-testid={`admin-loc-source-pill-${r.id}`}>{r.source}</span>
                )}
              </div>
            </div>
            <ChevronRight size={16} className="text-[#52525b] mt-1" />
          </button>
        ))}
        {!loading && rows.length === 0 && (
          <div className="sb-card p-8 text-center text-[#A1A1AA]">No locations yet.</div>
        )}
      </section>
      {newOpen && <NewLocationModal onClose={() => setNewOpen(false)} onCreated={(id) => { setNewOpen(false); load(); onOpen(id); }} />}
    </>
  );
}

// ---------- New Location Modal (owner-created) ----------
function NewLocationModal({ onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({
    business_name: "", address_line1: "", city: "", state: "", zip: "",
    manager_name: "", manager_title: "", manager_email: "", manager_phone: "",
    location_type: "", employees: "", hours: "",
    admin_notes: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function submit(ev) {
    ev.preventDefault();
    setErr("");
    if (!f.business_name.trim()) { setErr("Business name is required."); return; }
    setBusy(true);
    try {
      // Server is authoritative for source/scout_id — we do NOT send them.
      const body = {};
      for (const [k, v] of Object.entries(f)) {
        if (v === "" || v == null) continue;
        body[k] = v;
      }
      const r = await af("/api/admin/locations", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = (d && (d.detail || d.message)) || `HTTP ${r.status}`;
        setErr(`Could not create location: ${String(detail).slice(0, 300)}`);
        setBusy(false);
        return;
      }
      toast.success("Location created");
      onCreated?.(d.id);
    } catch { setErr("Could not create location: network error."); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !busy && onClose()} data-testid="admin-loc-new-modal">
      <div className="sb-card p-6 w-full max-w-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="sb-eyebrow">Owner-created</div>
            <div className="mt-1 text-[20px] font-black">New Location</div>
          </div>
          <button type="button" onClick={onClose} className="text-[#A1A1AA] hover:text-white" data-testid="admin-loc-new-close"><X size={18} /></button>
        </div>
        <div className="mt-2 text-[11.5px] text-[#A1A1AA]">
          This creates an <b className="text-white">Owner-sourced</b> location. No Scout is attached and no bounty ledger row is generated.
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <NewLocField label="Business name *" v={f.business_name} onChange={set("business_name")} testid="admin-loc-new-business" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <NewLocField label="Address" v={f.address_line1} onChange={set("address_line1")} testid="admin-loc-new-addr" />
            <div className="grid grid-cols-3 gap-3">
              <NewLocField label="City" v={f.city} onChange={set("city")} testid="admin-loc-new-city" />
              <NewLocField label="ST" v={f.state} onChange={set("state")} maxLength={2} testid="admin-loc-new-state" />
              <NewLocField label="ZIP" v={f.zip} onChange={set("zip")} testid="admin-loc-new-zip" />
            </div>
          </div>
          <div className="pt-3 border-t border-[#2A2F34]">
            <div className="sb-eyebrow mb-2">Authorized manager (optional at this step)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <NewLocField label="Manager name" v={f.manager_name} onChange={set("manager_name")} testid="admin-loc-new-mgr-name" />
              <NewLocField label="Manager title" v={f.manager_title} onChange={set("manager_title")} testid="admin-loc-new-mgr-title" />
              <NewLocField label="Manager email" v={f.manager_email} onChange={set("manager_email")} testid="admin-loc-new-mgr-email" type="email" />
              <NewLocField label="Manager phone" v={f.manager_phone} onChange={set("manager_phone")} testid="admin-loc-new-mgr-phone" />
            </div>
          </div>
          <div className="pt-3 border-t border-[#2A2F34]">
            <div className="sb-eyebrow mb-2">Optional details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <NewLocField label="Location type" v={f.location_type} onChange={set("location_type")} testid="admin-loc-new-type" placeholder="warehouse / office / apartment / etc." />
              <NewLocField label="Employees (approx)" v={f.employees} onChange={set("employees")} testid="admin-loc-new-emps" />
              <NewLocField label="Operating hours" v={f.hours} onChange={set("hours")} testid="admin-loc-new-hours" placeholder="e.g. 24/7 or Mon-Fri 8-6" />
              <NewLocField label="Internal notes" v={f.admin_notes} onChange={set("admin_notes")} testid="admin-loc-new-notes" />
            </div>
          </div>
          {err && <div className="text-[12px] text-[#EF4444]" data-testid="admin-loc-new-error">{err}</div>}
          <div className="pt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="sb-btn-secondary text-[12px]" style={{ padding: "8px 14px" }}>Cancel</button>
            <button type="submit" disabled={busy || !f.business_name.trim()} data-testid="admin-loc-new-submit" className="sb-btn-primary text-[12px] disabled:opacity-40" style={{ padding: "8px 16px" }}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create Location
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewLocField({ label, v, onChange, testid, maxLength, type = "text", placeholder }) {
  return (
    <label className="block">
      <div className="text-[10.5px] uppercase tracking-widest text-[#A1A1AA] mb-1">{label}</div>
      <input type={type} value={v} onChange={onChange} maxLength={maxLength} placeholder={placeholder}
        data-testid={testid}
        className="sb-input text-[13px]" />
    </label>
  );
}

// ---------- Detail ----------
const TABS = [
  { key: "overview", label: "Overview",       icon: Info },
  { key: "contact",  label: "Contact",        icon: User },
  { key: "traffic",  label: "Traffic & Place",icon: Users },
  { key: "photos",   label: "Photos",         icon: Info },
  { key: "terms",    label: "Terms",          icon: FileText },
  { key: "agreement",label: "Agreement",      icon: ShieldCheck },
  { key: "marketplace", label: "Marketplace", icon: Store },
  { key: "score",    label: "Score Data",     icon: Info },
  { key: "activity", label: "Activity",       icon: Activity },
  { key: "bounty",   label: "Bounty",         icon: Wallet },
];

function LocationDetail({ id, onBack }) {
  const [loc, setLoc] = useState(null);
  const [activity, setActivity] = useState([]);
  const [bounty, setBounty] = useState(null);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null); // {key,label,tone,detail}

  const load = useCallback(async () => {
    setLoading(true);
    const r = await af(`/api/admin/locations/${id}`);
    if (!r.ok) { toast.error("Location not found"); onBack(); return; }
    const d = await r.json();
    setLoc(d.location); setActivity(d.activity || []);
    // best-effort fetch of the bounty row for this location (single scout_id/location_id pair)
    if (d.location?.scout_id) {
      try {
        const br = await af(`/api/admin/bounty_ledger`);
        if (br.ok) {
          const bd = await br.json();
          const row = (bd.rows || []).find((x) => x.location_id === id);
          setBounty(row || null);
        }
      } catch { /* non-fatal */ }
    }
    setLoading(false);
  }, [id, onBack]);
  useEffect(() => { load(); }, [load]);

  async function runAction(action) {
    let path = `/api/admin/locations/${id}/status`;
    let body = {};
    if (action.key === "send_info") {
      path = `/api/admin/locations/${id}/send-info`;
    } else {
      body = { status: action.key };
    }
    const r = await af(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      toast.error(d?.detail || "Action failed");
      return;
    }
    if (action.key === "send_info") toast.success("Manager info sent");
    else toast.success(`Status updated: ${STATUS_META[action.key]?.label || action.key}`);
    setConfirmAction(null);
    await load();
    setTab((t) => (action.key === "send_info" ? "activity" : t));
  }

  if (loading || !loc) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#A1A1AA]"><Loader2 size={18} className="animate-spin text-[#FFC107]" /> Loading…</div>
      </div>
    );
  }

  const hasManagerEmail = Boolean((loc.manager_email || "").trim());

  return (
    <>
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }} data-testid="admin-loc-back">
          <ArrowLeft size={13} /> Back
        </button>
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="sb-headline text-[26px] md:text-[32px] truncate max-w-[560px]">{loc.business_name || "Untitled"}</h1>
            <StatusPill status={loc.status} />
          </div>
          <div className="text-[12px] text-[#A1A1AA] mt-1 flex items-center gap-3 flex-wrap">
            {loc.city && <span>{loc.city}{loc.state ? `, ${loc.state}` : ""}</span>}
            {loc.submitted_at && <span>Submitted {new Date(loc.submitted_at).toLocaleDateString()}</span>}
            <span>Scout: {loc.scout_name || loc.scout_email || "—"}</span>
          </div>
        </div>
      </div>

      {/* Action strip */}
      <section className="sb-card p-4">
        <div className="sb-eyebrow">Actions</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <ActionButton icon={CheckCircle2} label="Approve" tone="green"
                        onClick={() => setConfirmAction({ key: "verified", label: "Approve this location?", tone: "green", detail: "Marks it verified and moves the bounty ledger to qualified." })}
                        testid="admin-act-approve" />
          <ActionButton icon={AlertCircle} label="Request Info" tone="amber"
                        onClick={() => setConfirmAction({ key: "more_info_requested", label: "Request more info from Scout?", tone: "amber", detail: "The Scout can edit and re-submit." })}
                        testid="admin-act-more-info" />
          <ActionButton icon={XCircle} label="Reject" tone="red"
                        onClick={() => setConfirmAction({ key: "rejected", label: "Reject this location?", tone: "red", detail: "This is not reversible via the UI." })}
                        testid="admin-act-reject" />
          <div className="w-px bg-[#2A2F34] mx-1 self-stretch" />
          <ActionButton icon={ShieldCheck} label="Keep · SpotBounty" tone="gold"
                        onClick={() => setConfirmAction({ key: "decision_keep", label: "Keep for SpotBounty?", tone: "gold", detail: "Internal decision — records the intent to operate this location directly." })}
                        testid="admin-act-keep" />
          <ActionButton icon={ChevronRight} label="Prepare for Operator" tone="gold"
                        onClick={() => setConfirmAction({ key: "decision_operator", label: "Prepare for operator handoff?", tone: "gold", detail: "Internal decision — records intent to assign to an operator later." })}
                        testid="admin-act-operator" />
          <ActionButton icon={PauseCircle} label="Hold" tone="gray"
                        onClick={() => setConfirmAction({ key: "decision_hold", label: "Place this location on hold?", tone: "gray", detail: "Internal decision — pauses the workflow." })}
                        testid="admin-act-hold" />
          {hasManagerEmail && (
            <>
              <div className="w-px bg-[#2A2F34] mx-1 self-stretch" />
              <ActionButton icon={Send} label="Send Manager Info" tone="gold" primary
                            onClick={() => setConfirmAction({ key: "send_info", label: `Send manager review link?`, tone: "gold", detail: `Emails ${loc.manager_email} a secure review link for "${loc.business_name}" (24h expiry).` })}
                            testid="admin-act-send-info" />
            </>
          )}
        </div>
      </section>

      {/* Tabs */}
      <section className="sb-card p-0 overflow-hidden">
        <div className="flex overflow-x-auto border-b border-[#2A2F34] sb-scroll">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                      className={`flex items-center gap-2 px-5 py-3 text-[12px] font-bold uppercase tracking-widest border-b-2 transition-colors ${active ? "text-[#FFC107] border-[#FFC107]" : "text-[#A1A1AA] border-transparent hover:text-white"}`}
                      data-testid={`admin-loc-tab-${t.key}`}>
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>
        <div className="p-5">
          {tab === "overview"  && <OverviewTab loc={loc} />}
          {tab === "contact"   && <ContactTab  loc={loc} />}
          {tab === "traffic"   && <TrafficTab  loc={loc} />}
          {tab === "photos"    && <PhotosTab   id={id} />}
          {tab === "terms"     && <TermsTab    loc={loc} />}
          {tab === "agreement" && <AgreementTab loc={loc} id={id} onReload={load} />}
          {tab === "marketplace" && <ListingPrepTab loc={loc} id={id} onReload={load} />}
          {tab === "score"     && <ScoreTab    loc={loc} />}
          {tab === "activity"  && <ActivityTab activity={activity} />}
          {tab === "bounty"    && <BountyTab   bounty={bounty} scoutEmail={loc.scout_email} />}
        </div>
      </section>

      {/* Confirm modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setConfirmAction(null)}>
          <div className="sb-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="admin-loc-confirm">
            <div className="sb-eyebrow">Confirm action</div>
            <h3 className="mt-1 text-[18px] font-black">{confirmAction.label}</h3>
            <p className="mt-2 text-[13px] text-[#A1A1AA]">{confirmAction.detail}</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmAction(null)} className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }} data-testid="admin-loc-confirm-cancel">Cancel</button>
              <button onClick={() => runAction(confirmAction)} className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }} data-testid="admin-loc-confirm-ok">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ActionButton({ icon: Icon, label, onClick, tone = "gray", primary = false, testid }) {
  const toneClass = {
    green: "text-[#22C55E] border-[#22C55E]/40 hover:border-[#22C55E]",
    amber: "text-[#F59E0B] border-[#F59E0B]/40 hover:border-[#F59E0B]",
    red:   "text-[#EF4444] border-[#EF4444]/40 hover:border-[#EF4444]",
    gold:  "text-[#FFC107] border-[#FFC107]/40 hover:border-[#FFC107]",
    gray:  "text-[#A1A1AA] border-[#2A2F34] hover:border-white",
  }[tone];
  if (primary) {
    return (
      <button onClick={onClick} className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }} data-testid={testid}>
        <Icon size={13} /> {label}
      </button>
    );
  }
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-2 border rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors ${toneClass}`} data-testid={testid}>
      <Icon size={13} /> {label}
    </button>
  );
}

// ---------- Tabs ----------
function KV({ k, v, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="sb-eyebrow" style={{ fontSize: 9 }}>{k}</div>
      <div className="mt-1 text-[13px] text-white/90 break-words">{v == null || v === "" ? <span className="text-[#52525b]">—</span> : String(v)}</div>
    </div>
  );
}

function OverviewTab({ loc }) {
  return (
    <div className="grid md:grid-cols-3 gap-5">
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Business Summary</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <KV k="Business Name" v={loc.business_name} wide />
          <KV k="Business Type" v={loc.business_type} />
          <KV k="Status" v={STATUS_META[loc.status]?.label || loc.status} />
          <KV k="Decision" v={loc.decision || "—"} />
        </div>
      </div>
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Location &amp; Placement</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <KV k="Address" v={loc.address_line1} wide />
          <KV k="Address Line 2" v={loc.address_line2} wide />
          <KV k="City" v={loc.city} />
          <KV k="State" v={loc.state} />
          <KV k="ZIP" v={loc.zip} />
        </div>
      </div>
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Submission Details</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <KV k="Source" v={
            loc.source === "owner" ? (<span className="px-1.5 py-0.5 rounded border border-[#FFC107]/40 text-[#FFC107] bg-[#FFC107]/10 text-[10px] font-bold uppercase" data-testid="admin-loc-detail-source">Owner</span>) :
            loc.source === "business" ? (<span className="px-1.5 py-0.5 rounded border border-[#3B82F6]/40 text-[#3B82F6] bg-[#3B82F6]/10 text-[10px] font-bold uppercase" data-testid="admin-loc-detail-source">Business</span>) :
            (<span className="px-1.5 py-0.5 rounded border border-[#22C55E]/40 text-[#22C55E] bg-[#22C55E]/10 text-[10px] font-bold uppercase" data-testid="admin-loc-detail-source">Scout</span>)
          } wide />
          {loc.source === "scout" ? (
            <KV k="Scout" v={loc.scout_name || loc.scout_email} wide />
          ) : (
            <>
              <KV k="Scout" v={<span className="text-[#52525b]">None</span>} />
              <KV k="Bounty" v={<span className="text-[#52525b]">Not applicable</span>} />
            </>
          )}
          <KV k="Created" v={loc.created_at ? new Date(loc.created_at).toLocaleString() : "—"} />
          <KV k="Submitted" v={loc.submitted_at ? new Date(loc.submitted_at).toLocaleString() : "—"} />
          <KV k="Last Updated" v={loc.updated_at ? new Date(loc.updated_at).toLocaleString() : "—"} wide />
        </div>
      </div>
      {loc.admin_notes ? (
        <div className="sb-card p-4 md:col-span-3 border-[#F59E0B]/30" style={{ background: "rgba(245,158,11,.04)" }}>
          <div className="flex items-center gap-2">
            <Edit3 size={13} className="text-[#F59E0B]" />
            <div className="sb-eyebrow text-[#F59E0B]">Internal Admin Notes — Not Visible to Scout or Manager</div>
          </div>
          <div className="mt-2 text-[13px] text-white/90 whitespace-pre-wrap">{loc.admin_notes}</div>
        </div>
      ) : null}
    </div>
  );
}

function ContactTab({ loc }) {
  const hasAny = loc.manager_name || loc.manager_title || loc.manager_email || loc.manager_phone || loc.best_contact_time;
  if (!hasAny) {
    return <div className="text-[13px] text-[#A1A1AA] py-6 text-center">No manager contact info submitted yet.</div>;
  }
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Authorized Contact</div>
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-2"><User size={13} className="text-[#A1A1AA]" /><div className="text-[13px]">{loc.manager_name || <span className="text-[#52525b]">—</span>}</div></div>
          <div className="text-[12px] text-[#A1A1AA]">{loc.manager_title || ""}</div>
        </div>
      </div>
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Reach Out</div>
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-2"><Mail size={13} className="text-[#A1A1AA]" /><div className="text-[13px] break-all">{loc.manager_email || <span className="text-[#52525b]">—</span>}</div></div>
          <div className="flex items-center gap-2"><Phone size={13} className="text-[#A1A1AA]" /><div className="text-[13px]">{loc.manager_phone || <span className="text-[#52525b]">—</span>}</div></div>
          <div className="flex items-center gap-2"><Clock size={13} className="text-[#A1A1AA]" /><div className="text-[13px]">{loc.best_contact_time || <span className="text-[#52525b]">—</span>}</div></div>
        </div>
      </div>
    </div>
  );
}

function TrafficTab({ loc }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Traffic</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <KV k="Employees" v={loc.employees} />
          <KV k="Est. residents" v={loc.est_residents} />
          <KV k="Visitors / day" v={loc.visitors} />
          <KV k="Shifts" v={loc.shifts} />
          <KV k="Hours" v={loc.hours} />
          <KV k="Open 24/7" v={loc.open_24_7 ? "Yes" : "No"} />
          <KV k="Foot-traffic tier" v={loc.foot_traffic_tier} />
          <KV k="Weekend activity" v={loc.weekend_activity} />
        </div>
      </div>
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Existing Vending</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <KV k="Vending on-site" v={loc.vending_state} />
          <KV k="Machine types" v={(loc.existing_machine_types || []).join(", ")} wide />
          <KV k="Condition" v={loc.existing_machine_condition} />
          <KV k="Stocked" v={loc.existing_machine_stocked} />
          <KV k="Card payment" v={loc.existing_card_payment} />
          <KV k="Notes" v={loc.existing_vending_notes} wide />
        </div>
      </div>
      <div className="sb-card p-4 md:col-span-2">
        <div className="sb-eyebrow">Placement &amp; Environment</div>
        <div className="mt-3 grid md:grid-cols-4 grid-cols-2 gap-3">
          <KV k="Power outlet" v={loc.outlet_available == null ? null : (loc.outlet_available ? "Yes" : "No")} />
          <KV k="Wi-Fi" v={loc.wifi_available} />
          <KV k="Cellular" v={loc.cellular_signal} />
          <KV k="Indoor / Outdoor" v={loc.indoor_outdoor} />
          <KV k="Secure after hours" v={loc.secure_after_hours == null ? null : (loc.secure_after_hours ? "Yes" : "No")} />
          <KV k="Avail. width (in)" v={loc.available_width_in} />
          <KV k="Avail. depth (in)" v={loc.available_depth_in} />
          <KV k="Desired machines" v={(loc.desired_machine_types || []).join(", ")} />
          <KV k="Outlet notes" v={loc.outlet_notes} wide />
          <KV k="Placement notes" v={loc.placement_notes} wide />
        </div>
      </div>
    </div>
  );
}

function PhotosTab({ id }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    (async () => {
      const r = await af(`/api/admin/locations/${id}/photos`);
      const d = await r.json().catch(() => ({ rows: [] }));
      setRows(d.rows || []); setLoading(false);
    })();
  }, [id]);
  if (loading) return <div className="text-[#A1A1AA] flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</div>;
  if (rows.length === 0) return <div className="text-[13px] text-[#A1A1AA] py-6 text-center">No photos uploaded yet.</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {rows.map((p) => (
        <div key={p.id} className="sb-card p-2">
          <div className="aspect-square rounded-md overflow-hidden bg-[#0B0E11] border border-[#2A2F34]">
            {p.download_url ? <img src={p.download_url} alt="" className="w-full h-full object-cover" /> : null}
          </div>
          <div className="mt-2 text-[10.5px] uppercase tracking-widest text-[#FFC107]">{p.category?.replace(/_/g," ")}</div>
          {p.caption && <div className="text-[11.5px] text-white/80 mt-1 line-clamp-2">{p.caption}</div>}
          <div className="text-[10px] text-[#52525b] mt-1">{Math.round((p.size_bytes || 0)/1024)} KB · {new Date(p.uploaded_at).toLocaleDateString()}</div>
        </div>
      ))}
    </div>
  );
}

function ScoreTab({ loc }) {
  const snap = loc.location_score_inputs || {};
  const keys = [
    ["employees","Employees"],["est_residents","Residents"],["visitors","Visitors / day"],
    ["shifts","Shifts"],["hours","Hours"],["open_24_7","24/7"],
    ["business_type","Business type"],["foot_traffic_tier","Foot-traffic tier"],
    ["existing_machine_condition","Existing condition"],["dm_interest_level","DM interest"],
    ["financial_pref","Financial pref"],["requested_commission_pct","Requested %"],
    ["requested_monthly_amount","Requested $/mo"],["preferred_term_months","Term (months)"],
    ["exclusivity_pref","Exclusivity"],["preferred_opportunity_types","Opp types"],
    ["secure_after_hours","Secure after hrs"],["wifi_available","Wi-Fi"],
    ["outlet_available","Outlet"],
  ];
  return (
    <div className="sb-card p-5 border-[#F59E0B]/30" style={{ background: "rgba(245,158,11,.04)" }}>
      <div className="flex items-center gap-2">
        <div className="sb-eyebrow text-[#F59E0B]">Score Data — Internal Only</div>
      </div>
      <div className="mt-1 text-[12px] text-[#A1A1AA]">Snapshot of scoring-relevant fields from the latest save/submit. No computed score is shown or displayed publicly.</div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        {keys.map(([k, l]) => {
          const v = snap[k];
          const show = Array.isArray(v) ? v.join(", ") : (v == null || v === "") ? null : String(v);
          return <KV key={k} k={l} v={show} />;
        })}
      </div>
    </div>
  );
}

function TermsTab({ loc }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Contract Preferences</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <KV k="Financial" v={formatFinancialTerm(loc)} wide />
          <KV k="Contract term" v={formatContractTerm(loc)} wide />
          <KV k="Exclusivity" v={formatEnum(loc.exclusivity_pref)} />
          <KV k="Owner ready to sign" v={formatBool(loc.owner_willing_to_sign_now)} />
          <KV k="Preferred opportunities" v={formatArray(loc.preferred_opportunity_types)} wide />
          <KV k="Desired machine types" v={formatArray(loc.desired_machine_types)} wide />
        </div>
      </div>
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Decision Maker</div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <KV k="Spoken to DM" v={formatBool(loc.dm_was_spoken_to)} />
          <KV k="Preferred contact" v={formatEnum(loc.dm_preferred_contact)} />
          <KV k="Interest level" v={formatEnum(loc.dm_interest_level)} wide />
        </div>
        <div className="sb-eyebrow mt-5">General Notes</div>
        <div className="mt-2 text-[13px] text-white/90 whitespace-pre-wrap">{loc.general_notes || <span className="text-[#52525b]">—</span>}</div>
      </div>
    </div>
  );
}

const EVENT_META = {
  created:         { label: "Location Created",        icon: FileText,  tone: "text-[#A1A1AA]" },
  updated:         { label: "Updated",                 icon: Edit3,     tone: "text-[#A1A1AA]" },
  submitted:       { label: "Submitted by Scout",      icon: Send,      tone: "text-[#3B82F6]" },
  sent_to_manager: { label: "Manager Info Sent",       icon: Mail,      tone: "text-[#FFC107]" },
  status_changed:  { label: "Status Changed",          icon: Activity,  tone: "text-[#F59E0B]" },
  note_added:      { label: "Note Added",              icon: Edit3,     tone: "text-[#A1A1AA]" },
  photo_uploaded:  { label: "Photo Uploaded",          icon: FileText,  tone: "text-[#A1A1AA]" },
  agreement_draft_created:      { label: "Agreement Draft Created",   icon: FileText,    tone: "text-[#A1A1AA]" },
  agreement_updated:            { label: "Agreement Updated",         icon: Edit3,       tone: "text-[#A1A1AA]" },
  agreement_finalized:          { label: "Agreement Finalized",       icon: ShieldCheck, tone: "text-[#FFC107]" },
  agreement_sent:               { label: "Agreement Sent to Manager", icon: Send,        tone: "text-[#FFC107]" },
  agreement_viewed:             { label: "Agreement Viewed",          icon: Eye,         tone: "text-[#3B82F6]" },
  agreement_signed:             { label: "Agreement Signed",          icon: CheckCircle2,tone: "text-[#22C55E]" },
  agreement_revoked:            { label: "Agreement Revoked",         icon: XCircle,     tone: "text-[#EF4444]" },
  agreement_voided:             { label: "Agreement Voided",          icon: XCircle,     tone: "text-[#EF4444]" },
  agreement_expired:            { label: "Agreement Expired",         icon: Clock,       tone: "text-[#A1A1AA]" },
  agreement_pdf_generated:      { label: "Signed PDF Generated",      icon: Download,    tone: "text-[#22C55E]" },
  agreement_token_revoked:      { label: "Signing Link Revoked",      icon: XCircle,     tone: "text-[#F59E0B]" },
  agreement_new_version_created:{ label: "New Agreement Version",     icon: RotateCw,    tone: "text-[#A1A1AA]" },
};

function ActivityTab({ activity }) {
  if (!activity || activity.length === 0) {
    return <div className="text-[13px] text-[#A1A1AA] py-6 text-center">No activity yet.</div>;
  }
  return (
    <div className="space-y-3">
      {activity.map((a, i) => {
        const meta = EVENT_META[a.event_type] || { label: a.event_type, icon: Activity, tone: "text-[#A1A1AA]" };
        const Icon = meta.icon;
        const md = a.metadata || {};
        return (
          <div key={a.id || i} className="flex items-start gap-3 border-l-2 border-[#2A2F34] pl-4 py-1">
            <div className={`w-7 h-7 rounded-full border border-[#2A2F34] flex items-center justify-center ${meta.tone}`}>
              <Icon size={13} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold">{meta.label}</div>
              <div className="text-[11.5px] text-[#A1A1AA] mt-0.5">
                <Calendar size={11} className="inline mr-1 -mt-0.5" />
                {new Date(a.created_at).toLocaleString()} · by {a.actor_type}
              </div>
              {a.event_type === "status_changed" && (md.from || md.to) && (
                <div className="text-[11.5px] text-[#A1A1AA] mt-1">
                  {STATUS_META[md.from]?.label || md.from} → <b className="text-white">{STATUS_META[md.to]?.label || md.to}</b>
                  {md.note ? <span> · {md.note}</span> : null}
                </div>
              )}
              {a.event_type === "sent_to_manager" && md.manager_email && (
                <div className="text-[11.5px] text-[#A1A1AA] mt-1">to <span className="text-white">{md.manager_email}</span> · expires in {md.expires_hours || 24}h</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const BOUNTY_META = {
  potential:            { label: "Potential",             tone: "gray" },
  pending_verification: { label: "Pending Verification",  tone: "amber" },
  qualified:            { label: "Qualified",             tone: "green" },
  earned:               { label: "Earned",                tone: "green" },
  payment_pending:      { label: "Payment Pending",       tone: "amber" },
  paid:                 { label: "Paid",                  tone: "gold" },
  cancelled:            { label: "Cancelled",             tone: "red" },
};

function BountyTab({ bounty, scoutEmail }) {
  if (!bounty) {
    return (
      <div className="text-[13px] text-[#A1A1AA] py-6 text-center">
        No bounty ledger entry yet for this location.
      </div>
    );
  }
  const m = BOUNTY_META[bounty.status] || { label: bounty.status, tone: "gray" };
  const dollars = bounty.amount_cents != null ? `$${(bounty.amount_cents / 100).toFixed(2)}` : null;
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Scout</div>
        <div className="mt-2 text-[14px] font-bold">{bounty.scout_name || "—"}</div>
        <div className="text-[11.5px] text-[#A1A1AA] break-all">{scoutEmail || bounty.scout_email}</div>
      </div>
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Ledger Status</div>
        <div className="mt-2"><span className={`sb-pill sb-pill-${m.tone}`}>{m.label}</span></div>
      </div>
      <div className="sb-card p-4">
        <div className="sb-eyebrow">Amount</div>
        <div className="mt-2 text-[18px] font-black">{dollars || <span className="text-[#52525b] text-[14px]">Not set</span>}</div>
        {bounty.currency && dollars ? <div className="text-[11px] text-[#A1A1AA] mt-1">{bounty.currency}</div> : null}
      </div>
      <div className="sb-card p-4 md:col-span-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KV k="Earned" v={bounty.earned_at ? new Date(bounty.earned_at).toLocaleDateString() : null} />
          <KV k="Paid" v={bounty.paid_at ? new Date(bounty.paid_at).toLocaleDateString() : null} />
          <KV k="Ledger Created" v={bounty.created_at ? new Date(bounty.created_at).toLocaleDateString() : null} />
        </div>
      </div>
    </div>
  );
}


// ============================ AGREEMENT TAB (Slice 2) ============================

const AG_STATUS_PILL = {
  draft:     { label: "Draft",       tone: "gray"  },
  prepared:  { label: "Prepared",    tone: "amber" },
  sent:      { label: "Sent",        tone: "amber" },
  viewed:    { label: "Viewed",      tone: "blue"  },
  signed:    { label: "Signed",      tone: "green" },
  revoked:   { label: "Revoked",     tone: "red"   },
  voided:    { label: "Voided",      tone: "red"   },
  expired:   { label: "Expired",     tone: "gray"  },
};

function formatAgFinancial(ag) {
  if (!ag) return "—";
  if (ag.financial_pref === "revenue_share" && ag.requested_commission_pct != null)
    return `Revenue Share — ${ag.requested_commission_pct}%`;
  if (ag.financial_pref === "fixed_rent" && ag.requested_monthly_amount != null)
    return `$${ag.requested_monthly_amount}/month`;
  if (ag.financial_pref === "no_commission") return "No Location Commission";
  if (ag.financial_pref === "negotiable") return "Terms Negotiable";
  return "—";
}
function formatAgTerm(ag) {
  if (!ag) return "—";
  if (ag.preferred_term_months) return `${ag.preferred_term_months} months`;
  return ag.preferred_term_custom || "—";
}
function formatAgExcl(v) {
  return ({ requested: "Exclusive Requested", accepted: "Exclusive Accepted",
            declined: "Non-Exclusive", unknown: "To be discussed" }[v || "unknown"]);
}

function AgreementTab({ loc, id, onReload }) {
  const [ags, setAgs] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [editing, setEditing] = React.useState(null); // agreement being edited

  const reload = React.useCallback(async () => {
    setBusy(true);
    const r = await af(`/api/admin/locations/${id}/agreements`);
    if (r.ok) {
      const d = await r.json();
      setAgs(d.rows || []);
    } else {
      setAgs([]);
      if (r.status !== 404) toast.error("Could not load agreements");
    }
    setBusy(false);
  }, [id]);
  React.useEffect(() => { reload(); }, [reload]);

  const canPrepare = ["manager_interested", "agreement_pending"].includes(loc.status);
  const active = (ags || []).find((a) => !["revoked", "voided", "expired"].includes(a.status));
  const history = (ags || []).filter((a) => a !== active);

  async function prepare() {
    setBusy(true);
    const r = await af(`/api/admin/locations/${id}/agreements`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
    setBusy(false);
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d?.detail || "Could not prepare"); return; }
    toast.success("Draft agreement prepared");
    await reload();
    const nd = await r.json();
    setEditing(nd);
  }

  return (
    <div className="space-y-5" data-testid="admin-agreement-tab">
      <div className="border-2 border-[#F59E0B]/60 bg-[#F59E0B]/5 rounded-lg p-3 flex items-start gap-2">
        <AlertCircle size={16} className="text-[#F59E0B] mt-0.5 shrink-0" />
        <div className="text-[12.5px]">
          <b className="text-[#F59E0B]">TEST — NOT LEGALLY BINDING.</b> Preview agreements verify the e-sign workflow.
          Production agreements are gated behind attorney review and separate feature flags.
        </div>
      </div>

      {busy && !ags && <div className="flex items-center gap-2 text-[#A1A1AA] py-4"><Loader2 size={14} className="animate-spin" /> Loading…</div>}

      {ags && !active && (
        <div className="sb-card p-5 text-center">
          <div className="sb-eyebrow">No agreement prepared</div>
          <div className="mt-2 text-[13px] text-[#A1A1AA]">
            {canPrepare ? "You can prepare a draft test agreement for this location." : `Cannot prepare — location must be in "Manager Interested" or "Agreement Pending".`}
          </div>
          <button onClick={prepare} disabled={!canPrepare || busy}
                  className="sb-btn-primary text-[11px] mt-4"
                  style={{ padding: "12px 18px", opacity: !canPrepare ? 0.4 : 1 }}
                  data-testid="admin-ag-prepare">
            <FileText size={13} /> Prepare Agreement
          </button>
        </div>
      )}

      {active && editing?.id === active.id ? (
        <AgreementDraftForm ag={editing} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); await reload(); }} />
      ) : active ? (
        <AgreementActiveCard ag={active} loc={loc} onEdit={() => setEditing(active)} onChange={async () => { await reload(); await onReload?.(); }} />
      ) : null}

      {history.length > 0 && (
        <div className="sb-card p-4">
          <div className="sb-eyebrow">Previous versions</div>
          <div className="mt-3 space-y-2">
            {history.map((a) => (
              <div key={a.id} className="flex items-center justify-between border-t border-[#2A2F34] pt-2 text-[12px]">
                <div className="flex items-center gap-2">
                  <span className={`sb-pill sb-pill-${AG_STATUS_PILL[a.status]?.tone || "gray"}`}>{AG_STATUS_PILL[a.status]?.label || a.status}</span>
                  <span className="text-[#A1A1AA]">{a.reference_number || "—"}</span>
                </div>
                <div className="text-[#52525b] text-[11px]">{new Date(a.updated_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AgreementActiveCard({ ag, loc, onEdit, onChange }) {
  const [downloading, setDownloading] = React.useState(false);
  const [copyDone, setCopyDone] = React.useState(false);
  const [link, setLink] = React.useState(null);
  const [confirming, setConfirming] = React.useState(null);

  async function callApi(path, verb = "POST", body = {}) {
    const r = await af(path, { method: verb, headers: { "Content-Type": "application/json" }, body: verb === "GET" ? undefined : JSON.stringify(body) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d?.detail || "Action failed"); return null; }
    return await r.json().catch(() => ({}));
  }

  async function onFinalize() {
    if (!(await callApi(`/api/admin/agreements/${ag.id}/finalize`))) return;
    toast.success("Agreement finalized");
    onChange?.();
  }
  async function onSend() {
    const r = await callApi(`/api/admin/agreements/${ag.id}/send`);
    if (!r) return;
    setLink(r.link);
    toast.success("Signing link generated");
    onChange?.();
  }
  async function onRevoke() {
    if (!(await callApi(`/api/admin/agreements/${ag.id}/revoke`))) return;
    setConfirming(null); toast.success("Agreement revoked"); onChange?.();
  }
  async function onVoid() {
    if (!(await callApi(`/api/admin/agreements/${ag.id}/void`))) return;
    setConfirming(null); toast.success("Agreement voided"); onChange?.();
  }
  async function onDownloadPdf() {
    setDownloading(true);
    const r = await callApi(`/api/admin/agreements/${ag.id}/pdf`, "GET");
    setDownloading(false);
    if (r?.url) window.open(r.url, "_blank", "noopener");
  }
  async function onRegenPdf() {
    if (!(await callApi(`/api/admin/agreements/${ag.id}/regenerate-pdf`))) return;
    toast.success("PDF regenerated");
  }
  async function onCopy(txt) {
    try { await navigator.clipboard.writeText(txt); setCopyDone(true); setTimeout(() => setCopyDone(false), 1200); } catch { toast.error("Copy failed"); }
  }

  const pill = AG_STATUS_PILL[ag.status] || { label: ag.status, tone: "gray" };
  const isDraft = ag.status === "draft";
  const isPrepared = ag.status === "prepared";
  const isSent = ["sent", "viewed"].includes(ag.status);
  const isSigned = ag.status === "signed";

  return (
    <div className="space-y-4">
      {isSigned && (
        <div className="sb-card p-5 border-[#22C55E]/40" style={{ background: "rgba(34,197,94,.06)" }} data-testid="admin-ag-signed-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={18} className="text-[#22C55E]" />
                <div className="sb-eyebrow text-[#22C55E]">Signed Opportunity</div>
                <span className="sb-pill sb-pill-green">Signed</span>
              </div>
              <h3 className="mt-2 text-[20px] font-black truncate">{ag.business_name}</h3>
              <div className="text-[12px] text-[#A1A1AA]">{ag.business_address}</div>
              <div className="text-[12px] text-[#A1A1AA] mt-1">Reference: <b className="text-white">{ag.reference_number || "—"}</b></div>
              <div className="text-[12px] text-[#A1A1AA]">Signer: <b className="text-white">{ag.signer_name}</b>{ag.signer_title ? ` — ${ag.signer_title}` : ""}</div>
              <div className="text-[12px] text-[#A1A1AA]">Signed at: <b className="text-white">{ag.signed_at ? new Date(ag.signed_at).toLocaleString() : "—"}</b></div>
            </div>
            <button onClick={onDownloadPdf} disabled={downloading}
                    className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }} data-testid="admin-ag-download-pdf">
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download Signed PDF
            </button>
          </div>
          <div className="mt-4 grid md:grid-cols-3 gap-3 text-[12px]">
            <KV k="Machine types" v={formatArray(ag.machine_types)} />
            <KV k="Placement" v={ag.placement_description} wide />
            <KV k="Financial terms" v={formatAgFinancial(ag)} />
            <KV k="Contract term" v={formatAgTerm(ag)} />
            <KV k="Exclusivity" v={formatAgExcl(ag.exclusivity_pref)} />
            {ag.additional_terms && <KV k="Additional terms" v={ag.additional_terms} wide />}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={onRegenPdf} className="sb-btn-secondary text-[11px]" style={{ padding: "8px 12px" }} data-testid="admin-ag-regen-pdf">
              <RotateCw size={12} /> Regenerate PDF
            </button>
          </div>
        </div>
      )}

      <div className="sb-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className={`sb-pill sb-pill-${pill.tone}`} data-testid="admin-ag-status-pill">{pill.label}</span>
            {ag.reference_number && <span className="text-[12px] text-[#A1A1AA]">{ag.reference_number}</span>}
          </div>
          <div className="text-[11.5px] text-[#52525b]">Updated {new Date(ag.updated_at).toLocaleString()}</div>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <div className="sb-eyebrow">Business</div>
            <div className="text-[14px] font-bold mt-1">{ag.business_name || <span className="text-[#52525b]">—</span>}</div>
            <div className="text-[12px] text-[#A1A1AA]">{ag.business_address || ""}</div>
          </div>
          <div>
            <div className="sb-eyebrow">Signer</div>
            <div className="text-[14px] font-bold mt-1">{ag.signer_name || <span className="text-[#52525b]">—</span>}{ag.signer_title ? ` — ${ag.signer_title}` : ""}</div>
            <div className="text-[12px] text-[#A1A1AA] break-all">{ag.signer_email || ""}</div>
          </div>
        </div>

        <div className="mt-4 grid md:grid-cols-3 gap-3 text-[12px]">
          <KV k="Financial" v={formatAgFinancial(ag)} />
          <KV k="Term" v={formatAgTerm(ag)} />
          <KV k="Exclusivity" v={formatAgExcl(ag.exclusivity_pref)} />
          <KV k="Machine types" v={formatArray(ag.machine_types)} wide />
          <KV k="Placement" v={ag.placement_description} wide />
          {ag.additional_terms && <KV k="Additional terms" v={ag.additional_terms} wide />}
        </div>

        {(isSent || isSigned) && (
          <div className="mt-4 grid md:grid-cols-3 gap-3 text-[11.5px] text-[#A1A1AA] border-t border-[#2A2F34] pt-3">
            {ag.sent_at && <div>Sent <b className="text-white">{new Date(ag.sent_at).toLocaleString()}</b></div>}
            {ag.first_viewed_at && <div>First viewed <b className="text-white">{new Date(ag.first_viewed_at).toLocaleString()}</b></div>}
            {ag.last_viewed_at && <div>Last viewed <b className="text-white">{new Date(ag.last_viewed_at).toLocaleString()}</b></div>}
            {ag.expires_at && <div>Expires <b className="text-white">{new Date(ag.expires_at).toLocaleDateString()}</b></div>}
          </div>
        )}

        {link && (
          <div className="mt-4 border border-[#2A2F34] bg-[#0B0E11] rounded-lg p-3 flex items-center gap-2 text-[12px]" data-testid="admin-ag-signing-link">
            <ExternalLink size={13} className="text-[#FFC107] shrink-0" />
            <div className="flex-1 min-w-0 break-all font-mono text-[11.5px]">{link}</div>
            <button onClick={() => onCopy(link)} className="sb-btn-secondary text-[11px]" style={{ padding: "6px 10px" }} data-testid="admin-ag-copy-link">
              {copyDone ? <CheckCircle2 size={11} className="text-[#22C55E]" /> : <Copy size={11} />}
              {copyDone ? "Copied" : "Copy"}
            </button>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          {isDraft && (
            <>
              <button onClick={onEdit} className="sb-btn-secondary text-[11px]" style={{ padding: "9px 14px" }} data-testid="admin-ag-edit">
                <Edit3 size={12} /> Edit Draft
              </button>
              <button onClick={onFinalize} className="sb-btn-primary text-[11px]" style={{ padding: "9px 14px" }} data-testid="admin-ag-finalize">
                <ShieldCheck size={12} /> Finalize Agreement
              </button>
              <button onClick={() => setConfirming("void")} className="sb-btn-secondary text-[11px]" style={{ padding: "9px 14px", color: "#EF4444", borderColor: "rgba(239,68,68,.3)" }} data-testid="admin-ag-discard">
                <Trash2 size={12} /> Discard Draft
              </button>
            </>
          )}
          {isPrepared && (
            <>
              <button onClick={onSend} className="sb-btn-primary text-[11px]" style={{ padding: "9px 14px" }} data-testid="admin-ag-send">
                <Send size={12} /> Send Agreement
              </button>
              <button onClick={() => setConfirming("revoke")} className="sb-btn-secondary text-[11px]" style={{ padding: "9px 14px" }} data-testid="admin-ag-revoke">
                <XCircle size={12} /> Revoke
              </button>
            </>
          )}
          {isSent && (
            <>
              <button onClick={onSend} className="sb-btn-secondary text-[11px]" style={{ padding: "9px 14px" }} data-testid="admin-ag-regen-link">
                <RotateCw size={12} /> Generate Replacement Link
              </button>
              <button onClick={() => setConfirming("revoke")} className="sb-btn-secondary text-[11px]" style={{ padding: "9px 14px" }} data-testid="admin-ag-revoke">
                <XCircle size={12} /> Revoke
              </button>
            </>
          )}
        </div>

        {confirming && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setConfirming(null)}>
            <div className="sb-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-[18px] font-black">{confirming === "revoke" ? "Revoke this agreement?" : "Discard draft?"}</h3>
              <p className="mt-2 text-[13px] text-[#A1A1AA]">
                {confirming === "revoke" ? "The signing link becomes invalid immediately. Manager can no longer sign." : "The draft is voided. You can prepare a new one later."}
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setConfirming(null)} className="sb-btn-secondary text-[11px]" style={{ padding: "9px 14px" }}>Cancel</button>
                <button onClick={confirming === "revoke" ? onRevoke : onVoid} className="sb-btn-primary text-[11px]" style={{ padding: "9px 14px", background: "#EF4444", color: "#fff" }} data-testid="admin-ag-confirm">
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AgreementDraftForm({ ag, onCancel, onSaved }) {
  const [f, setF] = React.useState({
    business_name: ag.business_name || "", business_address: ag.business_address || "",
    signer_name: ag.signer_name || "", signer_title: ag.signer_title || "",
    signer_email: ag.signer_email || "", signer_phone: ag.signer_phone || "",
    machine_types: ag.machine_types || [],
    placement_description: ag.placement_description || "",
    financial_pref: ag.financial_pref || "revenue_share",
    requested_commission_pct: ag.requested_commission_pct ?? "",
    requested_monthly_amount: ag.requested_monthly_amount ?? "",
    preferred_term_months: ag.preferred_term_months || 12,
    preferred_term_custom: ag.preferred_term_custom || "",
    exclusivity_pref: ag.exclusivity_pref || "requested",
    additional_terms: ag.additional_terms || "",
  });
  const [saving, setSaving] = React.useState(false);

  function toggleMt(m) {
    setF((x) => ({ ...x, machine_types: x.machine_types.includes(m) ? x.machine_types.filter((v) => v !== m) : [...x.machine_types, m] }));
  }

  async function save(alsoFinalize) {
    setSaving(true);
    const payload = { ...f };
    ["requested_commission_pct","requested_monthly_amount","preferred_term_months"].forEach((k) => {
      if (payload[k] === "") payload[k] = null;
      else if (payload[k] != null) payload[k] = Number(payload[k]);
    });
    const r = await af(`/api/admin/agreements/${ag.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d?.detail || "Save failed"); setSaving(false); return; }
    if (alsoFinalize) {
      const r2 = await af(`/api/admin/agreements/${ag.id}/finalize`, { method: "POST" });
      if (!r2.ok) { const d = await r2.json().catch(() => ({})); toast.error(d?.detail || "Finalize failed"); setSaving(false); return; }
      toast.success("Agreement finalized");
    } else toast.success("Draft saved");
    setSaving(false);
    onSaved?.();
  }

  const MT = ["snack","drink","combo","smart_cooler","coffee","fresh_food","micro_market"];

  return (
    <div className="sb-card p-5 space-y-4" data-testid="admin-ag-draft-form">
      <div className="sb-eyebrow">Prepare Agreement — Draft</div>
      <div className="text-[11px] text-[#A1A1AA]">All fields will be frozen at finalization. Only canonical fields shown; legacy fields removed.</div>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Business name *"><input className="sb-input" value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="ag-f-biz" /></Field>
        <Field label="Business address"><input className="sb-input" value={f.business_address} onChange={(e) => setF({ ...f, business_address: e.target.value })} /></Field>
        <Field label="Signer name *"><input className="sb-input" value={f.signer_name} onChange={(e) => setF({ ...f, signer_name: e.target.value })} data-testid="ag-f-signer-name" /></Field>
        <Field label="Signer title"><input className="sb-input" value={f.signer_title} onChange={(e) => setF({ ...f, signer_title: e.target.value })} /></Field>
        <Field label="Signer email *"><input type="email" className="sb-input" value={f.signer_email} onChange={(e) => setF({ ...f, signer_email: e.target.value })} data-testid="ag-f-signer-email" /></Field>
        <Field label="Signer phone"><input className="sb-input" value={f.signer_phone} onChange={(e) => setF({ ...f, signer_phone: e.target.value })} /></Field>
      </div>

      <Field label="Machine types">
        <div className="flex flex-wrap gap-2 mt-1">
          {MT.map((m) => (
            <button key={m} type="button" onClick={() => toggleMt(m)}
                    className={`sb-pill ${f.machine_types.includes(m) ? "sb-pill-gold" : "sb-pill-gray"}`}>
              {m.replace("_", " ")}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Placement description">
        <textarea className="sb-input" rows={3} value={f.placement_description} onChange={(e) => setF({ ...f, placement_description: e.target.value })} />
      </Field>

      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Financial arrangement">
          <select className="sb-input" value={f.financial_pref} onChange={(e) => setF({ ...f, financial_pref: e.target.value })} data-testid="ag-f-financial">
            <option value="no_commission">No Location Commission</option>
            <option value="revenue_share">Revenue Share</option>
            <option value="fixed_rent">Fixed Monthly Payment</option>
            <option value="negotiable">Negotiable</option>
          </select>
        </Field>
        {f.financial_pref === "revenue_share" && (
          <Field label="Requested commission %">
            <input type="number" step="0.5" className="sb-input" value={f.requested_commission_pct} onChange={(e) => setF({ ...f, requested_commission_pct: e.target.value })} data-testid="ag-f-pct" />
          </Field>
        )}
        {f.financial_pref === "fixed_rent" && (
          <Field label="Monthly $">
            <input type="number" step="10" className="sb-input" value={f.requested_monthly_amount} onChange={(e) => setF({ ...f, requested_monthly_amount: e.target.value })} data-testid="ag-f-mo" />
          </Field>
        )}
        <Field label="Contract term (months)">
          <select className="sb-input" value={f.preferred_term_months || ""} onChange={(e) => setF({ ...f, preferred_term_months: e.target.value ? Number(e.target.value) : "" })} data-testid="ag-f-term">
            {[12, 24, 36, 48, 60].map((n) => <option key={n} value={n}>{n} months</option>)}
          </select>
        </Field>
      </div>

      <Field label="Exclusivity">
        <select className="sb-input" value={f.exclusivity_pref} onChange={(e) => setF({ ...f, exclusivity_pref: e.target.value })}>
          <option value="requested">Exclusive Requested</option>
          <option value="accepted">Exclusive Accepted</option>
          <option value="declined">Non-Exclusive</option>
          <option value="unknown">To be discussed</option>
        </select>
      </Field>

      <Field label="Additional negotiated terms">
        <textarea className="sb-input" rows={2} value={f.additional_terms} onChange={(e) => setF({ ...f, additional_terms: e.target.value })} />
      </Field>

      <div className="flex gap-2 justify-end pt-2 border-t border-[#2A2F34]">
        <button onClick={onCancel} className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }} disabled={saving}>Cancel</button>
        <button onClick={() => save(false)} className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }} disabled={saving} data-testid="admin-ag-save-draft">Save Draft</button>
        <button onClick={() => save(true)} className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }} disabled={saving} data-testid="admin-ag-save-finalize">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Save &amp; Finalize
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10.5px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-1">{label}</div>
      {children}
    </label>
  );
}
